import { MicVAD, utils } from '@ricky0123/vad-web';
import { makeLog } from 'shared/utils/log';
import { getBaseUrl } from 'shared/utils/sse';
import { OutboundTwilio } from './Twilio';
import { getTime } from 'date-fns';

type AudioRefs = OutboundTwilio;
const log = makeLog('Transcription');

/**
 * VAD is the service we use to chunk audio in sentences
 */
const VAD_SETTINGS = {
  redemptionFrames: 8,
  workletURL: `${process.env.CDN_URL}/vad.worklet.bundle.min.js`,
  modelURL: `${process.env.CDN_URL}/silero_vad.onnx`,
  ortConfig: (ort: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    ort.env.wasm.wasmPaths = {
      'ort-wasm-simd.wasm': `${process.env.CDN_URL}/ort-wasm-simd.wasm`,
      'ort-wasm-simd-threaded.wasm': `${process.env.CDN_URL}/ort-wasm-simd-threaded.wasm`,
      'ort-wasm.wasm': `${process.env.CDN_URL}/ort-wasm.wasm`,
      'ort-wasm-threaded.wasm': `${process.env.CDN_URL}/ort-wasm-threaded.wasm`,
    };
  },
};

/**
 * Transcribes the conversation by sending two audio tracks - inbound and outbound -
 * over a websocket to the backend.
 */
export const transcribe = async (audioReferences: AudioRefs, taskId: string) => {
  const retries = 10;

  const connect = () => {
    const websocketDomain = getBaseUrl()?.replace('http', 'ws');
    const WS_ENDPOINT = `${websocketDomain}/api/v1/outbound/dial_task/${taskId}/transcribe/`;
    return new WebSocket(WS_ENDPOINT);
  };

  const reconnect = () => {
    if (retries > 0) return setTimeout(() => reconnect, 1000);
    const errorMessage: any = { error: 'Failed to reconnect to SSE.' };
    log.error(errorMessage);
  };

  const socket = connect();
  socket.onerror = reconnect;
  audioReferences.call?.on('session.close', socket.close);
  audioReferences.call?.on('session.cancel', socket.close);
  audioReferences.call?.on('session.disconnect', socket.close);
  audioReferences.call?.on('session.error', reconnect);
  audioReferences.call?.on('session.reject', reconnect);

  /**
   * Get the inbound and outbound audio streams to transcribe.
   * The audio references getLocalStream() is not active so we get the microphone directly.
   * We use the audio reference to get the inbound audio.
   */
  const outboundStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const inboundStream = audioReferences.call?.getRemoteStream();

  // Track the actual start time of the audio chunk for each audio track
  let outboundStart: number;
  let inboundStart: number;

  /**
   * VAD is the audio detection service that helps chunk streaming audio into pieces.
   * We use these pieces to stream to the backend for transcription.
   *
   * We set up two: one for the inbound audio and one for the outbound audio
   */
  const inboundVad = await MicVAD.new({
    stream: inboundStream,
    onSpeechStart: () => {
      inboundStart = getTime(new Date());
    },
    onSpeechEnd: async (audio: Float32Array) => {
      const wavBuffer = utils.encodeWAV(audio);
      const encodedAudio = utils.arrayBufferToBase64(wavBuffer);
      const payload = {
        channel: 'inbound',
        audio: encodedAudio,
        startTime: inboundStart,
      };
      socket.send(JSON.stringify(payload));
    },
    ...VAD_SETTINGS,
  });

  const outboundVad = await MicVAD.new({
    stream: outboundStream,
    onSpeechStart: () => {
      outboundStart = getTime(new Date());
    },
    onSpeechEnd: audio => {
      const wavBuffer = utils.encodeWAV(audio);
      const encodedAudio = utils.arrayBufferToBase64(wavBuffer);
      const payload = {
        channel: 'outbound',
        audio: encodedAudio,
        startTime: outboundStart,
      };
      socket.send(JSON.stringify(payload));
    },
    ...VAD_SETTINGS,
  });

  inboundVad.start();
  outboundVad.start();
};
